/**
 * Bill accounting card — the double-entry ledger posting this bill made to the
 * books (Dr Purchases / Cr Accounts Payable, and Input GST when taxed). Reads
 * `accounting.entryForDocument`; renders an honest empty state for drafts
 * (which never post) and a reversal note for voided bills.
 *
 * @see prototype/purchases/bill-detail.html ▸ `.linked-strip` (chrome reused)
 */
import { BookOpen } from "lucide-react";
import { trpc } from "../../../lib/trpc.ts";
import { formatDate, paiseToInrExact } from "../../../lib/format.ts";

export interface BillJournalCardProps {
  billId: string;
  /** The bill's vendor — used to label the Accounts Payable sub-ledger line. */
  vendorName: string;
}

interface JournalLine {
  accountCode: string;
  accountName: string;
  accountType: string;
  debitPaise: string;
  creditPaise: string;
  partyType: "customer" | "vendor" | null;
  partyId: string | null;
}

interface JournalEntry {
  entryId: string;
  entryDate: string;
  narration: string;
  reversedByEntryId: string | null;
  isBalanced: boolean;
  lines: JournalLine[];
  totalDebitPaise: string;
  totalCreditPaise: string;
}

/** The accounting posting for a single bill, or an empty state when unposted. */
export function BillJournalCard({ billId, vendorName }: BillJournalCardProps) {
  const api = trpc as typeof trpc & Record<string, any>;
  const query = api.accounting.entryForDocument.useQuery({
    sourceType: "vendor_bill",
    sourceId: billId,
  });

  const entry = (query.data?.entry ?? null) as JournalEntry | null;

  return (
    <section className="linked-strip journal-strip" aria-label="Accounting">
      <div className="head">
        <BookOpen className="ic-h" size={18} aria-hidden />
        <h3>Accounting · Journal entry</h3>
      </div>

      {query.isLoading ? (
        <p className="empty">Loading the ledger posting…</p>
      ) : entry === null ? (
        <p className="empty">
          Not posted to the books yet — draft bills don&rsquo;t reach the ledger. The entry
          (Dr Purchases · Cr Accounts Payable) posts once the bill leaves draft.
        </p>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col" className="num">
                  Debit
                </th>
                <th scope="col" className="num">
                  Credit
                </th>
              </tr>
            </thead>
            <tbody>
              {entry.lines.map((line, i) => (
                <tr key={`${line.accountCode}-${i}`}>
                  <td>
                    <span className="acct">{line.accountName}</span>
                    {line.partyType === "vendor" ? (
                      <span className="party"> · {vendorName}</span>
                    ) : null}
                  </td>
                  <td className="num">{line.debitPaise === "0" ? "—" : paiseToInrExact(line.debitPaise)}</td>
                  <td className="num">
                    {line.creditPaise === "0" ? "—" : paiseToInrExact(line.creditPaise)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{paiseToInrExact(entry.totalDebitPaise)}</td>
                <td className="num">{paiseToInrExact(entry.totalCreditPaise)}</td>
              </tr>
            </tfoot>
          </table>
          <p className="note">
            {entry.narration} · posted {formatDate(entry.entryDate)}
            {entry.reversedByEntryId
              ? " · reversed (this bill was voided; a balancing entry was posted)"
              : entry.isBalanced
                ? " · balanced"
                : ""}
          </p>
        </>
      )}
    </section>
  );
}
